import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import topGradient from '@/assets/top-gradient.png';
import { useToast } from '@/hooks/use-toast';
import { mergeSettingsCache } from '@/hooks/useSettings';
import { CLIENT_ID_BLOOMKEEP } from '@/lib/constants';
import { writeThemeCacheForSlug } from '@/lib/themeCache';
import { getNetlifyFunctionsRoot } from '@/lib/utils';
import {
  buildCustomThemeSelection,
  normalizeThemeRow,
  normalizeThemeSelection,
} from '@/lib/themeResolver';
import type {
  NormalizedTheme,
  NormalizedThemePreset,
  ThemeFonts,
  ThemeSeed,
  ThemeSelection,
} from '@/types/theme';
import { usePanelBreadcrumb } from '../../components/PanelBreadcrumbContext';
import { getEventBadgeState } from '../../components/EventPulsingBadge';
import { parseEventDateValue } from '../../utils/dateTime';
import { buildDefaultSettingsValues, EVENT_DETAILS_SETTINGS_KEYS, EventSettingsKey, SharedSettingKey, isSupportedLanguageValue } from './settingsSchema';

type UserData = {
  display_name: string;
  avatar_url: string;
  id: string;
  email?: string;
};

type Client = {
  id: string;
  user_id: string;
  created_at?: string;
  updated_at?: string;
  name?: string | null;
  email?: string | null;
};

type EventRow = {
  id: string;
  slug: string;
  event_date?: string;
  is_active: boolean;
  is_reserved?: boolean;
  client_id?: string | null;
  created_at?: string;
  updated_at?: string;
  internal_notes?: string | null;
};

type EventStatus = 'active' | 'reserved' | 'inactive';

type EventStats = {
  photos: number;
  users: number;
};

type EventDetailsContextValue = {
  eventId: string;
  event: EventRow | null;
  loading: boolean;
  title: string;
  albumUrl: string | null;
  qrUrl: string | null;
  badgeState: ReturnType<typeof getEventBadgeState>;
  panelRole: 'superadmin' | 'admin';
  status: EventStatus;
  handleStatusChange: (value: EventStatus) => Promise<void>;
  eventDate: Date | null;
  draftEventDate: Date | null;
  setDraftEventDate: React.Dispatch<React.SetStateAction<Date | null>>;
  handleDateSave: () => Promise<void>;
  savingDate: boolean;
  photoCount: number | null;
  userCount: number | null;
  clientUser: UserData | null;
  clientDisplayName: string;
  clientEmailDisplay: string;
  clientAvatarInitial: string;
  assignOpen: boolean;
  setAssignOpen: React.Dispatch<React.SetStateAction<boolean>>;
  clients: Client[];
  clientUsers: Map<string, UserData>;
  filteredClients: Client[];
  clientQuery: string;
  setClientQuery: React.Dispatch<React.SetStateAction<string>>;
  selectedClientId: string | null;
  setSelectedClientId: React.Dispatch<React.SetStateAction<string | null>>;
  savingAssignment: boolean;
  openAssignModal: () => Promise<void>;
  confirmAssign: () => Promise<void>;
  notesDraft: string;
  setNotesDraft: React.Dispatch<React.SetStateAction<string>>;
  notesDirty: boolean;
  notesSaving: boolean;
  handleNotesSave: () => Promise<void>;
  handleNotesCancel: () => void;
  coverImageUrl: string;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  newCoverPreviewUrl: string | null;
  savingCover: boolean;
  openCoverFileDialog: () => void;
  onCoverFileChange: React.ChangeEventHandler<HTMLInputElement>;
  onApplyNewCover: () => Promise<void>;
  resetNewCover: () => void;
  qrRef: React.RefObject<HTMLDivElement | null>;
  handleDownloadQr: () => void;
  copyAlbumUrl: () => Promise<void>;
  copyEventId: () => Promise<void>;
  deleteDialogOpen: boolean;
  setDeleteDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleConfirmDelete: () => Promise<void>;
  settingsValues: Record<SharedSettingKey, string>;
  getSettingValue: (key: SharedSettingKey) => string;
  updateSetting: (key: SharedSettingKey, newValue: string) => Promise<boolean>;
  toggleSetting: (key: EventSettingsKey) => Promise<void>;
  saveLanguageSetting: (nextLanguage: string) => Promise<boolean>;
  savingLanguage: boolean;
  themeSelection: ThemeSelection | null;
  normalizedTheme: NormalizedTheme;
  themeDisplayName: string;
  themePresets: NormalizedThemePreset[];
  themeLoading: boolean;
  themeSaving: boolean;
  savePresetTheme: (themeId: string) => Promise<boolean>;
  saveCustomTheme: (seed: ThemeSeed, fonts?: ThemeFonts) => Promise<boolean>;
};

const EventDetailsContext = createContext<EventDetailsContextValue | undefined>(undefined);

const DEFAULT_SETTINGS_VALUES = buildDefaultSettingsValues();

async function updateEventStatus(eventId: string, status: EventStatus): Promise<void> {
  const is_active = status === 'active';
  const is_reserved = status === 'reserved';

  const { error } = await supabase
    .from('albums')
    .update({ is_active, is_reserved } as never)
    .eq('id', eventId);

  if (error) throw error;
}

async function updateEventDate(eventId: string, date: Date | null): Promise<void> {
  const { error } = await supabase
    .from('albums')
    .update({ event_date: date ? date.toISOString() : null } as never)
    .eq('id', eventId);

  if (error) throw error;
}

async function fetchEventStats(eventId: string): Promise<EventStats> {
  const mediaPromise = supabase
    .rpc('get_media_with_votes_filtered', { p_album_id: eventId, p_exclude_flagged: false, p_include_pending: false } as never);

  const usersPromise = supabase
    .rpc('get_users_with_roles', { album_id_param: eventId });

  const [{ data: mediaRows, error: mediaError }, { data: userRows, error: usersError }] = await Promise.all([
    mediaPromise,
    usersPromise,
  ]);

  if (mediaError) throw mediaError;
  if (usersError) throw usersError;

  const filteredUsers = (userRows || []).filter((user: any) => ['admin', 'user'].includes(user?.role));

  return {
    photos: (mediaRows ?? []).length,
    users: filteredUsers.length,
  };
}

async function loadAssignedClient(clientId: string): Promise<{ client: Client | null; clientUser: UserData | null }> {
  const { data: client } = await (supabase as any)
    .from('clients')
    .select('id, user_id, created_at, updated_at, name, email')
    .eq('id', clientId)
    .maybeSingle();

  if (!client) {
    return { client: null, clientUser: null };
  }

  const { data: userData } = await supabase.rpc('get_user_display_info', {
    user_ids: [(client as any).user_id],
  });

  return {
    client: client as Client,
    clientUser: userData && userData.length > 0 ? userData[0] : null,
  };
}

function fallbackTitleFromSlug(slug: string): string {
  return slug
    .split('-')
    .map((word) => (word ? word[0].toUpperCase() + word.slice(1) : ''))
    .join(' ');
}

export const STATUS_OPTIONS: Array<{ value: EventStatus; label: string }> = [
  { value: 'active', label: 'Activo' },
  { value: 'reserved', label: 'Reservado' },
  { value: 'inactive', label: 'Inactivo' },
];

export const EventDetailsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { setTrail } = usePanelBreadcrumb();
  const { toast } = useToast();
  const [event, setEvent] = useState<EventRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [panelRole, setPanelRole] = useState<'superadmin' | 'admin'>('admin');
  const [settingsValues, setSettingsValues] = useState<Record<SharedSettingKey, string>>(() => buildDefaultSettingsValues());
  const [status, setStatus] = useState<EventStatus>('inactive');
  const [eventDate, setEventDate] = useState<Date | null>(null);
  const [draftEventDate, setDraftEventDate] = useState<Date | null>(null);
  const [savingDate, setSavingDate] = useState(false);
  const [photoCount, setPhotoCount] = useState<number | null>(null);
  const [userCount, setUserCount] = useState<number | null>(null);
  const [notesValue, setNotesValue] = useState('');
  const [notesDraft, setNotesDraft] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [clientUsers, setClientUsers] = useState<Map<string, UserData>>(new Map());
  const [clientQuery, setClientQuery] = useState('');
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [savingAssignment, setSavingAssignment] = useState(false);
  const [client, setClient] = useState<Client | null>(null);
  const [clientUser, setClientUser] = useState<UserData | null>(null);
  const [newCoverFile, setNewCoverFile] = useState<File | null>(null);
  const [newCoverPreviewUrl, setNewCoverPreviewUrl] = useState<string | null>(null);
  const [savingCover, setSavingCover] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [savingLanguage, setSavingLanguage] = useState(false);
  const [themeSelection, setThemeSelection] = useState<ThemeSelection | null>(null);
  const [normalizedTheme, setNormalizedTheme] = useState<NormalizedTheme>(() => normalizeThemeSelection(null));
  const [themePresets, setThemePresets] = useState<NormalizedThemePreset[]>([]);
  const [themeLoading, setThemeLoading] = useState(true);
  const [themeSaving, setThemeSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const qrRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      if (!id) {
        setLoading(false);
        return;
      }

      setLoading(true);
      if (newCoverPreviewUrl) {
        try {
          URL.revokeObjectURL(newCoverPreviewUrl);
        } catch {
          // no-op
        }
      }
      setAssignOpen(false);
      setDeleteDialogOpen(false);
      setClientQuery('');
      setSelectedClientId(null);
      setNewCoverFile(null);
      setNewCoverPreviewUrl(null);
      setPhotoCount(null);
      setUserCount(null);
      setSettingsValues(buildDefaultSettingsValues());
      setThemeLoading(true);
      setThemeSelection(null);
      setNormalizedTheme(normalizeThemeSelection(null));
      setThemePresets([]);

      try {
        const { data: eventRow, error: eventError } = await supabase
          .from('albums')
          .select('id, slug, event_date, is_active, is_reserved, client_id, created_at, updated_at, internal_notes')
          .eq('id', id)
          .maybeSingle();

        if (eventError) throw eventError;

        if (!eventRow) {
          if (!isMounted) return;
          setEvent(null);
          setClient(null);
          setClientUser(null);
          return;
        }

        const nextEvent = eventRow as EventRow;
        const nextStatus: EventStatus = nextEvent.is_active ? 'active' : (nextEvent.is_reserved ? 'reserved' : 'inactive');
        const nextEventDate = parseEventDateValue(nextEvent.event_date);
        const nextNotes = nextEvent.internal_notes ?? '';

        const [
          { data: settingsRows, error: settingsError },
          { data: themeSelectionRow, error: themeSelectionError },
          { data: presetRows, error: presetError },
          assignedClient,
        ] = await Promise.all([
          supabase
            .from('settings')
            .select('key, value')
            .eq('album_id', id)
            .in('key', EVENT_DETAILS_SETTINGS_KEYS),
          supabase
            .from('settings')
            .select('value')
            .eq('album_id', id)
            .eq('key', 'theme_selection')
            .maybeSingle(),
          supabase
            .from('themes')
            .select('id, name, colors, fonts, seed, sort_order')
            .is('album_id', null)
            .order('sort_order')
            .order('name'),
          nextEvent.client_id ? loadAssignedClient(nextEvent.client_id) : Promise.resolve({ client: null, clientUser: null }),
        ]);

        if (settingsError) throw settingsError;
        if (themeSelectionError && themeSelectionError.code !== 'PGRST116') throw themeSelectionError;
        if (presetError) throw presetError;
        if (!isMounted) return;

        const nextSettings = buildDefaultSettingsValues();
        (settingsRows ?? []).forEach((row) => {
          const nextValue = typeof row.value === 'string' ? row.value : String(row.value ?? '');
          if (row.key === 'lang' && !isSupportedLanguageValue(nextValue)) {
            return;
          }
          nextSettings[row.key as SharedSettingKey] = nextValue;
        });

        const nextThemePresets: NormalizedThemePreset[] = (presetRows ?? []).map((preset) => {
          const normalizedPreset = normalizeThemeRow(preset);
          return {
            ...normalizedPreset,
            themeId: preset.id,
            name: preset.name,
            sortOrder: preset.sort_order,
          };
        });

        let nextThemeSelection: ThemeSelection | null = null;
        let nextNormalizedTheme = normalizeThemeSelection(null);

        if (themeSelectionRow?.value) {
          try {
            const parsedSelection = JSON.parse(themeSelectionRow.value) as ThemeSelection;
            nextThemeSelection = parsedSelection;

            if (parsedSelection.type === 'preset') {
              const matchedPreset = (presetRows ?? []).find((preset) => preset.id === parsedSelection.theme_id) || null;
              nextNormalizedTheme = normalizeThemeSelection(parsedSelection, matchedPreset);
            } else {
              nextNormalizedTheme = normalizeThemeSelection(parsedSelection);
            }
          } catch (error) {
            console.error('Failed to parse theme selection', error);
          }
        }

        setEvent(nextEvent);
        setStatus(nextStatus);
        setEventDate(nextEventDate);
        setDraftEventDate(nextEventDate);
        setNotesValue(nextNotes);
        setNotesDraft(nextNotes);
        setSettingsValues(nextSettings);
        setClient(assignedClient.client);
        setClientUser(assignedClient.clientUser);
        setThemeSelection(nextThemeSelection);
        setNormalizedTheme(nextNormalizedTheme);
        setThemePresets(nextThemePresets);
      } catch (error) {
        console.error('Failed to load event', error);
      } finally {
        if (isMounted) {
          setLoading(false);
          setThemeLoading(false);
        }
      }
    };

    void load();

    return () => {
      isMounted = false;
    };
  }, [id]);

  useEffect(() => {
    if (!id) return;

    const checkRole = async () => {
      try {
        const { data: superData } = await supabase.rpc('is_superadmin', { album_id: id });
        if (superData === true) {
          setPanelRole('superadmin');
          return;
        }

        const { data: adminData } = await supabase.rpc('is_admin', { album_id: id });
        setPanelRole(adminData === true ? 'admin' : 'admin');
      } catch (error) {
        setPanelRole('admin');
      }
    };

    void checkRole();
  }, [id]);

  useEffect(() => {
    if (!event?.id) return;

    let isMounted = true;

    const loadStats = async () => {
      try {
        const stats = await fetchEventStats(event.id);
        if (!isMounted) return;
        setPhotoCount(stats.photos);
        setUserCount(stats.users);
      } catch (error) {
        console.error('Failed to fetch event stats', error);
        if (!isMounted) return;
        setPhotoCount(0);
        setUserCount(0);
      }
    };

    void loadStats();

    return () => {
      isMounted = false;
    };
  }, [event?.id]);

  useEffect(() => {
    return () => {
      if (newCoverPreviewUrl) {
        try {
          URL.revokeObjectURL(newCoverPreviewUrl);
        } catch {
          // no-op
        }
      }
    };
  }, [newCoverPreviewUrl]);

  const fallbackTitle = useMemo(() => fallbackTitleFromSlug(event?.slug || ''), [event?.slug]);
  const title = useMemo(() => {
    const customTitle = settingsValues.text_home_h1?.trim();
    return customTitle ? customTitle : fallbackTitle;
  }, [fallbackTitle, settingsValues.text_home_h1]);
  const coverImageUrl = useMemo(() => {
    return settingsValues.image_cover || topGradient;
  }, [settingsValues.image_cover]);
  const albumUrl = useMemo(() => {
    return event?.slug ? `/${event.slug}` : null;
  }, [event?.slug]);
  const qrUrl = useMemo(() => {
    return event?.slug ? `https://app.bloomkeep.site/${event.slug}?source=qr` : null;
  }, [event?.slug]);
  const badgeState = useMemo(() => {
    return getEventBadgeState(eventDate ?? event?.event_date ?? null);
  }, [eventDate, event?.event_date]);

  useEffect(() => {
    setTrail([
      { label: 'Panel', to: '/panel' },
      { label: 'Eventos', to: '/panel/eventos/listado' },
      { label: title || 'Evento' },
    ]);

    return () => {
      setTrail(null);
    };
  }, [setTrail, title]);

  const filteredClients = useMemo(() => {
    const query = clientQuery.trim().toLowerCase();
    if (!query) return clients;

    return clients.filter((clientRow) => {
      const userData = clientUsers.get(clientRow.user_id);
      const displayName = userData?.display_name || clientRow.name || clientRow.email || '';
      const email = userData?.email || clientRow.email || '';
      return displayName.toLowerCase().includes(query) || email.toLowerCase().includes(query);
    });
  }, [clientQuery, clientUsers, clients]);

  const clientDisplayName = clientUser?.display_name || client?.name || client?.email || 'Sin asignar';
  const clientEmailDisplay = clientUser?.email || client?.email || '';
  const clientAvatarInitial = clientDisplayName.trim().charAt(0).toUpperCase() || '?';
  const notesDirty = notesDraft !== notesValue;
  const themeDisplayName = useMemo(() => {
    if (themeSelection?.type === 'preset') {
      const matchedPreset = themePresets.find((preset) => preset.themeId === themeSelection.theme_id);
      return matchedPreset?.name || normalizedTheme.name || 'Tema predefinido';
    }

    if (themeSelection?.type === 'custom') {
      return 'Tema personalizado';
    }

    return normalizedTheme.name || 'BloomKeep';
  }, [normalizedTheme.name, themePresets, themeSelection]);

  const getSettingValue = (key: SharedSettingKey) => {
    const value = settingsValues[key] ?? DEFAULT_SETTINGS_VALUES[key];
    if (key === 'lang' && !isSupportedLanguageValue(value)) {
      return DEFAULT_SETTINGS_VALUES.lang;
    }
    return value;
  };

  const persistSetting = async (key: SharedSettingKey, newValue: string, options?: { updateLocal?: boolean }) => {
    if (!id) return false;

    try {
      const { data, error } = await supabase
        .from('settings')
        .update({ value: newValue } as never)
        .eq('album_id', id)
        .eq('key', key)
        .select();

      if (error) throw error;

      if (!data || data.length === 0) {
        const { error: insertError } = await supabase
          .from('settings')
          .insert({ album_id: id, key, value: newValue } as never);

        if (insertError) throw insertError;
      }

      if (options?.updateLocal !== false) {
        setSettingsValues((prev) => ({ ...prev, [key]: newValue }));
      }

      mergeSettingsCache(id, { [key]: newValue });

      return true;
    } catch (error) {
      Sentry.captureMessage(`EventDetails setting upsert: ${error instanceof Error ? error.message : String(error)}`, 'error');
      console.error('Failed to update setting', error);
      return false;
    }
  };

  const updateSetting = async (key: SharedSettingKey, newValue: string) => {
    if (key === 'lang' && !isSupportedLanguageValue(newValue)) {
      return false;
    }
    return persistSetting(key, newValue);
  };

  const toggleSetting = async (key: EventSettingsKey) => {
    const currentValue = getSettingValue(key);
    const nextValue = currentValue === 'true' ? 'false' : 'true';

    setSettingsValues((prev) => ({ ...prev, [key]: nextValue }));

    const saved = await persistSetting(key, nextValue, { updateLocal: false });
    if (!saved) {
      setSettingsValues((prev) => ({ ...prev, [key]: currentValue }));
    }
  };

  const saveLanguageSetting = async (nextLanguage: string) => {
    if (!isSupportedLanguageValue(nextLanguage)) {
      toast({
        title: 'Error al guardar',
        description: 'El idioma seleccionado no es valido.',
        variant: 'destructive',
      });
      return false;
    }

    setSavingLanguage(true);

    try {
      const saved = await persistSetting('lang', nextLanguage);
      if (!saved) {
        toast({
          title: 'Error al guardar',
          description: 'No pudimos actualizar el idioma. Intenta nuevamente.',
          variant: 'destructive',
        });
        return false;
      }

      toast({
        title: 'Configuracion actualizada',
        description: 'El idioma se guardo correctamente. Recargando...',
      });

      window.setTimeout(() => {
        window.location.reload();
      }, 300);

      return true;
    } catch (error) {
      toast({
        title: 'Error al guardar',
        description: 'No pudimos actualizar el idioma. Intenta nuevamente.',
        variant: 'destructive',
      });
      return false;
    } finally {
      setSavingLanguage(false);
    }
  };

  const persistThemeSelection = async (selection: ThemeSelection, nextTheme: NormalizedTheme) => {
    if (!id) return false;

    setThemeSaving(true);

    try {
      const { error } = await supabase
        .from('settings')
        .upsert({
          album_id: id,
          key: 'theme_selection',
          value: JSON.stringify(selection),
          label: 'Selección de Tema',
        } as never);

      if (error) throw error;

      const selectionKey = JSON.stringify(selection);
      setThemeSelection(selection);
      setNormalizedTheme(nextTheme);
      mergeSettingsCache(id, { theme_selection: selectionKey });
      writeThemeCacheForSlug({
        slug: event?.slug,
        selectionKey,
        themeId: selection.theme_id,
        tokens: nextTheme.tokens,
      });

      return true;
    } catch (error) {
      Sentry.captureMessage(`EventDetails theme upsert: ${error instanceof Error ? error.message : String(error)}`, 'error');
      console.error('Failed to persist theme selection', error);
      toast({
        title: 'Error al guardar',
        description: 'No pudimos actualizar el tema. Intentá nuevamente.',
        variant: 'destructive',
      });
      return false;
    } finally {
      setThemeSaving(false);
    }
  };

  const savePresetTheme = async (themeId: string) => {
    const preset = themePresets.find((item) => item.themeId === themeId);
    if (!preset) return false;

    const selection: ThemeSelection = {
      type: 'preset',
      theme_id: themeId,
    };

    const saved = await persistThemeSelection(selection, preset);
    if (saved) {
      toast({
        title: 'Tema actualizado',
        description: `"${preset.name}" quedó aplicado al evento.`,
      });
    }

    return saved;
  };

  const saveCustomTheme = async (seed: ThemeSeed, fonts?: ThemeFonts) => {
    const selection = buildCustomThemeSelection(seed, fonts);
    const nextTheme = normalizeThemeSelection(selection);
    const saved = await persistThemeSelection(selection, nextTheme);

    if (saved) {
      toast({
        title: 'Tema actualizado',
        description: 'Los cambios de colores y tipografías se guardaron correctamente.',
      });
    }

    return saved;
  };

  const handleStatusChange = async (value: EventStatus) => {
    if (!event?.id) return;

    try {
      await updateEventStatus(event.id, value);
      setStatus(value);
      toast({
        title: 'Estado actualizado',
        description: 'El estado del evento se guardó correctamente.',
      });
    } catch (error) {
      console.error('Failed to update event status', error);
      toast({
        title: 'Error al guardar',
        description: 'No pudimos actualizar el estado. Intentá nuevamente.',
        variant: 'destructive',
      });
    }
  };

  const handleDateSave = async () => {
    if (!event?.id) return;

    setSavingDate(true);

    try {
      await updateEventDate(event.id, draftEventDate);
      setEventDate(draftEventDate);
      toast({
        title: 'Fecha actualizada',
        description: 'La fecha del evento se guardó correctamente.',
      });
    } catch (error) {
      console.error('No se pudo guardar la fecha del evento', error);
      toast({
        title: 'Error al guardar',
        description: 'No pudimos guardar la nueva fecha. Intentá nuevamente.',
        variant: 'destructive',
      });
    } finally {
      setSavingDate(false);
    }
  };

  const openAssignModal = async () => {
    setAssignOpen(true);

    if (clients.length > 0) {
      return;
    }

    const { data: clientsData } = await (supabase as any)
      .from('clients')
      .select('id, user_id, created_at, updated_at, name, email')
      .neq('id', CLIENT_ID_BLOOMKEEP)
      .order('created_at', { ascending: false });

    const clientsList = (clientsData as Client[]) || [];
    setClients(clientsList);

    if (clientsList.length === 0) {
      return;
    }

    const userIds = clientsList.map((clientRow) => clientRow.user_id);
    const { data: usersData } = await supabase.rpc('get_user_display_info', {
      user_ids: userIds,
    });

    if (!usersData) {
      return;
    }

    const usersMap = new Map<string, UserData>();
    usersData.forEach((userData: UserData) => {
      usersMap.set(userData.id, userData);
    });
    setClientUsers(usersMap);
  };

  const confirmAssign = async () => {
    if (!id || !selectedClientId) return;

    setSavingAssignment(true);

    try {
      const { error } = await supabase
        .from('albums')
        .update({ client_id: selectedClientId } as never)
        .eq('id', id);

      if (error) throw error;

      const nextClient = clients.find((clientRow) => clientRow.id === selectedClientId) || null;
      setClient(nextClient);

      if (nextClient) {
        setClientUser(clientUsers.get(nextClient.user_id) || null);
      } else {
        setClientUser(null);
      }

      setAssignOpen(false);
      setSelectedClientId(null);
      setClientQuery('');
    } catch (error) {
      console.error('Failed to assign client', error);
    } finally {
      setSavingAssignment(false);
    }
  };

  const handleNotesSave = async () => {
    if (!event?.id) return;

    setNotesSaving(true);

    try {
      const { error } = await supabase
        .from('albums')
        .update({ internal_notes: notesDraft } as never)
        .eq('id', event.id);

      if (error) throw error;

      setNotesValue(notesDraft);
      toast({
        title: 'Notas guardadas',
        description: 'Las notas internas se actualizaron correctamente.',
      });
    } catch (error) {
      console.error('No se pudieron guardar las notas', error);
      toast({
        title: 'Error al guardar',
        description: 'No pudimos guardar las notas internas. Intentá de nuevo.',
        variant: 'destructive',
      });
    } finally {
      setNotesSaving(false);
    }
  };

  const handleNotesCancel = () => {
    setNotesDraft(notesValue);
  };

  const openCoverFileDialog = () => {
    try {
      fileInputRef.current?.click();
    } catch {
      // no-op
    }
  };

  const resetNewCover = () => {
    if (newCoverPreviewUrl) {
      try {
        URL.revokeObjectURL(newCoverPreviewUrl);
      } catch {
        // no-op
      }
    }

    setNewCoverFile(null);
    setNewCoverPreviewUrl(null);
  };

  const onCoverFileChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const file = event.target.files?.[0] || null;
    event.currentTarget.value = '';

    if (!file) return;

    const type = (file.type || '').toLowerCase();
    const isJpg = type === 'image/jpeg' || type === 'image/jpg';
    const isPng = type === 'image/png';

    if (!isJpg && !isPng) {
      toast({
        title: 'Formato no soportado',
        description: 'Usá una imagen JPG o PNG.',
        variant: 'destructive',
      });
      return;
    }

    const maxBytes = 1 * 1024 * 1024;
    if (file.size > maxBytes) {
      toast({
        title: 'Archivo demasiado pesado',
        description: 'El archivo supera el tamaño máximo de 1 MB.',
        variant: 'destructive',
      });
      return;
    }

    const previewUrl = URL.createObjectURL(file);

    if (newCoverPreviewUrl) {
      try {
        URL.revokeObjectURL(newCoverPreviewUrl);
      } catch {
        // no-op
      }
    }

    setNewCoverFile(file);
    setNewCoverPreviewUrl(previewUrl);
  };

  const onApplyNewCover = async () => {
    if (!id || !newCoverFile) return;

    setSavingCover(true);

    try {
      const mapTypeToExt = (type: string) => {
        if (type.includes('jpeg') || type.includes('jpg')) return '.jpg';
        if (type.includes('png')) return '.png';
        return '.jpg';
      };

      const lowerName = (newCoverFile.name || '').toLowerCase();
      const hasExt = /\.(jpg|jpeg|png)$/.test(lowerName);
      const ext = hasExt ? lowerName.substring(lowerName.lastIndexOf('.')) : mapTypeToExt(newCoverFile.type || '');
      const fileName = `cover_${Date.now()}${ext}`;

      const response = await fetch(`${getNetlifyFunctionsRoot()}/upload-to-cdn?fileName=${encodeURIComponent(fileName)}&albumId=${encodeURIComponent(id)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/octet-stream' },
        body: newCoverFile,
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.url) {
        throw new Error((data && (data.body || data.error || data.message)) || 'Error al subir la imagen al CDN');
      }

      const saved = await updateSetting('image_cover', data.url);
      if (!saved) {
        throw new Error('No pudimos guardar la portada en la configuración.');
      }

      resetNewCover();
      toast({
        title: 'Portada actualizada',
        description: 'La imagen de portada se guardó correctamente.',
      });
    } catch (error) {
      console.error('Error actualizando portada', error);
      toast({
        title: 'Error al actualizar',
        description: 'No pudimos actualizar la portada. Intentá nuevamente.',
        variant: 'destructive',
      });
    } finally {
      setSavingCover(false);
    }
  };

  const handleDeleteEvent = async () => {
    if (!event?.id) return;

    try {
      const { error } = await supabase.from('albums').delete().eq('id', event.id);
      if (error) throw error;

      toast({
        title: 'Evento eliminado',
        description: 'El evento fue borrado correctamente.',
      });
      navigate('/panel/eventos/listado');
    } catch (error) {
      console.error('Error eliminando el evento', error);
      toast({
        title: 'Error al eliminar',
        description: 'No pudimos borrar el evento. Intentá nuevamente.',
        variant: 'destructive',
      });
    }
  };

  const handleConfirmDelete = async () => {
    await handleDeleteEvent();
    setDeleteDialogOpen(false);
  };

  const handleDownloadQr = () => {
    if (!qrRef.current || !qrUrl || !event?.slug) return;

    const canvas = qrRef.current.querySelector('canvas') as HTMLCanvasElement | null;
    if (!canvas) return;

    const size = 1000;
    const tmp = document.createElement('canvas');
    tmp.width = size;
    tmp.height = size;

    const context = tmp.getContext('2d');
    if (!context) return;

    context.clearRect(0, 0, size, size);
    // @ts-expect-error imageSmoothingEnabled exists on the 2d context.
    context.imageSmoothingEnabled = false;
    context.drawImage(canvas, 0, 0, canvas.width, canvas.height, 0, 0, size, size);

    const dataUrl = tmp.toDataURL('image/png');
    const anchor = document.createElement('a');
    anchor.href = dataUrl;
    anchor.download = `BK_${event.slug}-codigo-QR.png`;
    anchor.click();
  };

  const copyAlbumUrl = async () => {
    if (!albumUrl) return;

    try {
      await navigator.clipboard.writeText(`https://app.bloomkeep.site${albumUrl}`);
      toast({
        title: 'Enlace copiado',
        description: 'El enlace del evento se copió al portapapeles.',
      });
    } catch (error) {
      console.error('No se pudo copiar el enlace del evento', error);
      toast({
        title: 'Error al copiar',
        description: 'No pudimos copiar el enlace. Intentá nuevamente.',
        variant: 'destructive',
      });
    }
  };

  const copyEventId = async () => {
    if (!event?.id) return;

    try {
      await navigator.clipboard.writeText(event.id);
      toast({
        title: 'ID copiado',
        description: 'El identificador interno se copió al portapapeles.',
      });
    } catch (error) {
      console.error('No se pudo copiar el identificador interno', error);
      toast({
        title: 'Error al copiar',
        description: 'No pudimos copiar el identificador. Intentá nuevamente.',
        variant: 'destructive',
      });
    }
  };

  const value: EventDetailsContextValue = {
    eventId: id ?? '',
    event,
    loading,
    title,
    albumUrl,
    qrUrl,
    badgeState,
    panelRole,
    status,
    handleStatusChange,
    eventDate,
    draftEventDate,
    setDraftEventDate,
    handleDateSave,
    savingDate,
    photoCount,
    userCount,
    clientUser,
    clientDisplayName,
    clientEmailDisplay,
    clientAvatarInitial,
    assignOpen,
    setAssignOpen,
    clients,
    clientUsers,
    filteredClients,
    clientQuery,
    setClientQuery,
    selectedClientId,
    setSelectedClientId,
    savingAssignment,
    openAssignModal,
    confirmAssign,
    notesDraft,
    setNotesDraft,
    notesDirty,
    notesSaving,
    handleNotesSave,
    handleNotesCancel,
    coverImageUrl,
    fileInputRef,
    newCoverPreviewUrl,
    savingCover,
    openCoverFileDialog,
    onCoverFileChange,
    onApplyNewCover,
    resetNewCover,
    qrRef,
    handleDownloadQr,
    copyAlbumUrl,
    copyEventId,
    deleteDialogOpen,
    setDeleteDialogOpen,
    handleConfirmDelete,
    settingsValues,
    getSettingValue,
    updateSetting,
    toggleSetting,
    saveLanguageSetting,
    savingLanguage,
    themeSelection,
    normalizedTheme,
    themeDisplayName,
    themePresets,
    themeLoading,
    themeSaving,
    savePresetTheme,
    saveCustomTheme,
  };

  return (
    <EventDetailsContext.Provider value={value}>
      {children}
    </EventDetailsContext.Provider>
  );
};

export function useEventDetails() {
  const context = useContext(EventDetailsContext);
  if (!context) {
    throw new Error('useEventDetails must be used within an EventDetailsProvider');
  }
  return context;
}
